import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import _, { isEmpty } from 'lodash'
import { Button, Col, Form, Icon, Input, Modal, Select, Row, Upload, Radio, Tooltip, DatePicker } from 'antd'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { FormItem } from '../../../../styles/shared.style'
import { ButtonEx } from '../../../../styles/button.style'
import { useDeepEffect } from '../../../../utils/hooks'
import { beforeUpload, compressFile, getBase64 } from '../../../../utils/services'
import { selectRule } from '../../../../utils/rules'
import { REWARD_CATAGORY, REWARD_TYPE, COUPON_DEFAULT } from '../../../../utils/constants'
import { fetchGainTypeList } from '../../../../modules/reward'
import ScopeRuleBuilder from '../ScopeRuleBuilder'
import axios from 'axios'

const LIMIT_TYPE = {
  UNLIMITED: { value: 'unlimited', label: 'ไม่จำกัด' },
  CUSTOM: { value: 'custom', label: 'กำหนดเอง' },
  DISABLED: { value: 'disabled', label: 'ปิดการแลกชั่วคราว' }
}

const SCOPE_MODE = {
  UNLIMITED: 'unlimited',
  CUSTOM: 'custom'
}

const RewardForm = (props) => {
  const { visible, onOk, onCancel, formData, form, condition } = props
  const { getFieldDecorator, validateFields, setFieldsValue } = form
  const dispatch = useDispatch()
  const [imageUrl, setImageUrl] = useState('')
  const [file, setFile] = useState(null)
  const hasPrefilledRef = useRef(false)
  const [limitType, setLimitType] = useState(LIMIT_TYPE.UNLIMITED)
  const [rewardType, setRewardType] = useState(REWARD_TYPE.PRODUCT.value)
  const [scopeMode, setScopeMode] = useState(SCOPE_MODE.UNLIMITED)
  const [scopeRules, setScopeRules] = useState([])
  // ไม่แตะเงื่อนไข = ไม่ส่ง field ไป backend เลย (ค่าเดิมไม่เปลี่ยน)
  const [scopeRulesDirty, setScopeRulesDirty] = useState(false)

  const gainTypeList = useSelector((state) => state.reward.gainPointTypeList, [])

  useDeepEffect(() => {
    dispatch(fetchGainTypeList())
  }, [])

  function changeScopeRules(next) {
    setScopeRules(next)
    setScopeRulesDirty(true)
  }

  function changeScopeMode(mode) {
    if (mode === SCOPE_MODE.UNLIMITED && scopeRules.length > 0) {
      Modal.confirm({
        title: 'ล้างเงื่อนไขทั้งหมด?',
        content: 'เปลี่ยนเป็นไม่จำกัดจะลบเงื่อนไขที่ตั้งไว้ทั้งหมด',
        okText: 'ล้างเงื่อนไข',
        cancelText: 'ยกเลิก',
        onOk: () => {
          setScopeMode(mode)
          changeScopeRules([])
        }
      })
      return
    }
    setScopeMode(mode)
    if (mode === SCOPE_MODE.CUSTOM && scopeRules.length === 0) {
      changeScopeRules([{ gainTypeId: undefined, channel: null, heroId: null, activityId: null, brandId: null }])
    }
  }

  useDeepEffect(() => {
    if (!isEmpty(formData)) {
      setFieldsValue({
        name: formData.name,
        detail: formData.detail,
        redeemPoint: formData.redeemPoint,
        availableAmount: formData.availableAmount,
        catagories: formData.catagories,
        // รางวัลเก่าบางรายการไม่มี condition — เดิมอ่าน .id ตรง ๆ แล้วฟอร์มพัง
        condition: formData.condition ? formData.condition.id : undefined,
        couponValidDays: formData.couponValidDays,
        couponOpenWindowSec: formData.couponOpenWindowSec,
        availableFrom: formData.availableFrom ? moment(formData.availableFrom) : undefined,
        availableUntil: formData.availableUntil ? moment(formData.availableUntil) : undefined
      })
      setImageUrl(formData.image)
      // ไม่มี rewardType = รางวัลเก่า ถือเป็นสินค้า
      setRewardType(formData.rewardType || REWARD_TYPE.PRODUCT.value)
      const existingRules = formData.scopeRules || []
      setScopeRules(existingRules)
      setScopeMode(existingRules.length > 0 ? SCOPE_MODE.CUSTOM : SCOPE_MODE.UNLIMITED)
      setScopeRulesDirty(false)
      if (Number(formData.maxRedeemPerUser) === 0 && formData.maxRedeemPerUser != null) {
        setLimitType(LIMIT_TYPE.DISABLED)
      } else if (formData.maxRedeemPerUser != null) {
        setLimitType(LIMIT_TYPE.CUSTOM)
      } else {
        setLimitType(LIMIT_TYPE.UNLIMITED)
      }
    }
    return () => {
      form.resetFields()
      setImageUrl('')
      setFile(null)
      hasPrefilledRef.current = false
      setLimitType(LIMIT_TYPE.UNLIMITED)
      setRewardType(REWARD_TYPE.PRODUCT.value)
      setScopeMode(SCOPE_MODE.UNLIMITED)
      setScopeRules([])
      setScopeRulesDirty(false)
    }
  }, [formData])

  useDeepEffect(() => {
    if (limitType === LIMIT_TYPE.CUSTOM && !hasPrefilledRef.current && !isEmpty(formData) && formData.maxRedeemPerUser != null && Number(formData.maxRedeemPerUser) !== 0) {
      hasPrefilledRef.current = true
      setTimeout(() => {
        setFieldsValue({ maxRedeemPerUser: formData.maxRedeemPerUser })
      }, 0)
    }
  }, [limitType])

  function onSubmit(e) {
    e.preventDefault()
    validateFields(async (err, values) => {
      if (!err) {
        const isEdit = !_.isEmpty(formData)

        if (scopeMode === SCOPE_MODE.CUSTOM) {
          const missingGainType = scopeRules.some((rule) => !rule.gainTypeId)
          if (scopeRules.length === 0 || missingGainType) {
            Modal.error({
              title: 'เงื่อนไขยังไม่ครบ',
              content: 'ทุกเงื่อนไขต้องเลือกประเภทการได้พอยท์ และต้องมีอย่างน้อย 1 เงื่อนไข'
            })
            return
          }
          const seen = new Set()
          const duplicated = scopeRules.some((rule) => {
            const key = [rule.gainTypeId, rule.channel, rule.heroId, rule.activityId, rule.brandId].join('|')
            if (seen.has(key)) {
              return true
            }
            seen.add(key)
            return false
          })
          if (duplicated) {
            Modal.error({ title: 'เงื่อนไขซ้ำกัน', content: 'มีเงื่อนไขที่ตั้งค่าเหมือนกันมากกว่าหนึ่งแถว' })
            return
          }
        }

        if (values.availableFrom && values.availableUntil
            && values.availableFrom.isAfter(values.availableUntil)) {
          Modal.error({ title: 'ช่วงเวลาไม่ถูกต้อง', content: 'วันเริ่มต้องมาก่อนวันสิ้นสุด' })
          return
        }

        const data = {
          name: values.name,
          detail: values.detail,
          redeemPoint: values.redeemPoint,
          availableAmount: values.availableAmount,
          catagories: values.catagories,
          conditionId: values.condition,
          rewardType: rewardType,
          availableFrom: values.availableFrom ? values.availableFrom.toISOString() : null,
          availableUntil: values.availableUntil ? values.availableUntil.toISOString() : null
        }

        if (rewardType === REWARD_TYPE.COUPON.value) {
          data.couponValidDays = Number(values.couponValidDays || COUPON_DEFAULT.VALID_DAYS)
          data.couponOpenWindowSec = Number(values.couponOpenWindowSec || COUPON_DEFAULT.OPEN_WINDOW_SEC)
        }

        // ช่องที่เว้นว่างต้องส่ง null ไม่ใช่ 0 หรือสตริงว่าง
        if (!isEdit || scopeRulesDirty) {
          data.scopeRules = scopeMode === SCOPE_MODE.UNLIMITED
            ? []
            : scopeRules.map((rule) => ({
              gainTypeId: rule.gainTypeId,
              channel: rule.channel || null,
              heroId: rule.heroId || null,
              activityId: rule.activityId || null,
              brandId: rule.brandId || null
            }))
        }

        const original = isEdit ? formData.maxRedeemPerUser : undefined
        const maxValue = limitType === LIMIT_TYPE.DISABLED ? 0
          : limitType === LIMIT_TYPE.CUSTOM ? Number(values.maxRedeemPerUser)
          : null

        if (!isEdit) {
          if (maxValue != null) data.maxRedeemPerUser = maxValue
        } else if (maxValue !== original) {
          data.maxRedeemPerUser = maxValue
        }
        if (values.image) {
          try {
            const formImgData = new FormData()
            if (!_.isNull(values.image.file)) {
              formImgData.append('image', await compressFile(values.image.file))
              const res = await axios.post(
                process.env.REACT_APP_API_ENDPOINT + `/reward/image`,
                formImgData,
                {
                  headers: {
                    'content-type': 'multipart/form-data'
                  }
                }
              )
              data.image = res.data.url
            }
          } catch (err) {
            console.log(err)
          }
        } else {
          data.image = formData.image
        }

        if (!_.isEmpty(formData)) {
          data.id = formData.id
        }
        onOk(data)
      }
    })
  }
  async function handleChange(info) {
    await getBase64(info.file, (imageUrl) => {
      setImageUrl(imageUrl)
    })
  }

  const uploadButton = (
    <div>
      <Icon type="plus" />
      <div className="ant-upload-text">Upload</div>
    </div>
  )

  return (
    <Modal
      title={`${!_.isEmpty(formData) ? 'Edit' : 'Create'} a Rewards`}
      visible={visible}
      footer={null}
      onCancel={onCancel}>
      <Form form={form} onSubmit={onSubmit} layout="vertical">
        <Form.Item label="ชื่อรางวัล">
          {getFieldDecorator(`name`, {
            rules: [
              {
                required: true,
                message: 'กรุณากรอก'
              }
            ]
          })(<Input
            name="name"
            placeholder="ชื่อรางวัล" />)}
        </Form.Item>
        <Form.Item label="รายละเอียด">
          {getFieldDecorator(`detail`, {
            rules: [
              {
                required: true,
                message: 'กรุณากรอก'
              }
            ]
          })(<Input.TextArea
            name="detail"
            placeholder="รายละเอียด" />)}
        </Form.Item>
        <FormItem label="รูปรางวัล">
          {getFieldDecorator(`image`, {
            rules: [
              {
                required: _.isEmpty(formData),
                message: 'กรุณาเลือก'
              }
            ]
          })(
            <Upload
              name="img"
              listType="picture-card"
              showUploadList={false}
              beforeUpload={(file) => beforeUpload(file)}
              onChange={handleChange}>
              {imageUrl ? (
                <img src={imageUrl} alt="img" style={{ width: '100%' }} />
              ) : (
                uploadButton
              )}
            </Upload>
          )}
        </FormItem>
        <Form.Item label="คะแนนที่ใช้แลก">
          {getFieldDecorator(`redeemPoint`, {
            rules: [
              {
                required: true,
                message: 'กรุณากรอก'
              }
            ]
          })(<Input type="number"
            name="redeemPoint"
            placeholder="คะแนนที่ใช้แลก" />)}
        </Form.Item>
        <Form.Item label="จำนวนของรางวัล">
          {getFieldDecorator(`availableAmount`, {
            rules: [
              {
                required: true,
                message: 'กรุณากรอก'
              }
            ]
          })(<Input type="number"
            name="availableAmount"
            placeholder="จำนวนของรางวัล" />)}
        </Form.Item>
        <Form.Item label="ชนิดรางวัล">
          <Radio.Group
            value={rewardType}
            onChange={(e) => setRewardType(e.target.value)}>
            <Radio value={REWARD_TYPE.PRODUCT.value}>{REWARD_TYPE.PRODUCT.label}</Radio>
            <Radio value={REWARD_TYPE.COUPON.value}>{REWARD_TYPE.COUPON.label}</Radio>
          </Radio.Group>
          {rewardType === REWARD_TYPE.COUPON.value && (
            <div style={{ marginTop: 8 }}>
              <div>อายุคูปองหลังแลก (วัน)</div>
              {getFieldDecorator('couponValidDays', {
                initialValue: COUPON_DEFAULT.VALID_DAYS
              })(<Input type="number" min={1} placeholder="เช่น 30" />)}
              <div style={{ marginTop: 8 }}>เวลาใช้งานหลังกดเปิด (วินาที)</div>
              {getFieldDecorator('couponOpenWindowSec', {
                initialValue: COUPON_DEFAULT.OPEN_WINDOW_SEC
              })(<Input type="number" min={60} placeholder="1800 = 30 นาที" />)}
              <div style={{ color: '#999', marginTop: 4 }}>
                คูปองไม่ต้องใช้ที่อยู่จัดส่ง ผู้ใช้แลกได้ทันทีโดยไม่ต้องเพิ่มที่อยู่
              </div>
            </div>
          )}
        </Form.Item>
        <Form.Item label="เปิดให้แลกตั้งแต่ (เว้นว่าง = ทันที)">
          {getFieldDecorator('availableFrom')(
            <DatePicker showTime style={{ width: '100%' }} placeholder="เลือกวันเวลาเริ่ม" />
          )}
        </Form.Item>
        <Form.Item label="เปิดให้แลกถึง (เว้นว่าง = ไม่จำกัด)">
          {getFieldDecorator('availableUntil')(
            <DatePicker showTime style={{ width: '100%' }} placeholder="เลือกวันเวลาสิ้นสุด" />
          )}
        </Form.Item>
        <Form.Item label="ประเภทของรางวัล">
          {getFieldDecorator(`catagories`, { ...selectRule })(
            <Select placeholder="เลือกประเภทของรางวัล">
              {REWARD_CATAGORY.map((item) => (
                <Select.Option key={item.value} value={item.value}>
                  {item.label}
                </Select.Option>
              ))}
            </Select>
          )}
        </Form.Item>
        <Form.Item label="เงื่อนไข">
          {getFieldDecorator(`condition`, {
            rules: [
              {
                required: true,
                message: 'กรุณาเลือก'
              }
            ]
          })(
            <Radio.Group buttonStyle="solid">
              {condition &&
                condition.map((condition) => (
                  <Radio.Button value={condition.id}>
                    <Tooltip title={condition.detail}>
                      <div style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
                        {condition.id}
                      </div>
                    </Tooltip>
                  </Radio.Button>
                ))}
            </Radio.Group>
          )}
        </Form.Item>
        <Form.Item label="จำนวนสูงสุดต่อคน">
          <Radio.Group
            value={limitType}
            onChange={(e) => setLimitType(e.target.value)}>
            <Radio value={LIMIT_TYPE.UNLIMITED}>{LIMIT_TYPE.UNLIMITED.label}</Radio>
            <Radio value={LIMIT_TYPE.CUSTOM}>{LIMIT_TYPE.CUSTOM.label}</Radio>
            <Radio value={LIMIT_TYPE.DISABLED}>{LIMIT_TYPE.DISABLED.label}</Radio>
          </Radio.Group>
          {limitType === LIMIT_TYPE.CUSTOM && (
            <div style={{ marginTop: 8 }}>
              {getFieldDecorator('maxRedeemPerUser', {
                rules: [{
                  required: true,
                  message: 'กรุณากรอกจำนวน'
                }, {
                  validator: (rule, value, callback) => {
                    if (value !== undefined && value !== '' && Number(value) < 1) {
                      callback('จำนวนต้องมากกว่า 0')
                    } else {
                      callback()
                    }
                  }
                }]
              })(
                <Input
                  type="number"
                  min={1}
                  name="maxRedeemPerUser"
                  placeholder="กรอกจำนวนสูงสุด (ขั้นต่ำ 1)" />
              )}
            </div>
          )}
        </Form.Item>
        <Form.Item label="เงื่อนไขการแลก">
          <Radio.Group
            value={scopeMode}
            onChange={(e) => changeScopeMode(e.target.value)}>
            <Radio value={SCOPE_MODE.UNLIMITED}>ไม่จำกัด — แลกได้ด้วยพอยท์ทุกประเภท</Radio>
            <Radio value={SCOPE_MODE.CUSTOM}>กำหนดเงื่อนไข</Radio>
          </Radio.Group>
          {scopeMode === SCOPE_MODE.CUSTOM && (
            <div style={{ marginTop: 8 }}>
              <ScopeRuleBuilder
                value={scopeRules}
                onChange={changeScopeRules}
                gainTypeList={gainTypeList}
              />
              {!_.isEmpty(formData) && (
                <div style={{ color: '#999', marginTop: 8 }}>
                  การแก้เงื่อนไขมีผลกับการแลกครั้งถัดไปเท่านั้น ไม่ย้อนหลังกับคนที่แลกไปแล้ว
                </div>
              )}
            </div>
          )}
        </Form.Item>
        <Row type="flex" gutter={8} justify="end">
          <Col>
            <Button onClick={() => onCancel()}>ยกเลิก</Button>
          </Col>
          <Col>
            <ButtonEx color="#6ebf4b" type="primary" htmlType="submit">
              บันทึก
            </ButtonEx>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}

RewardForm.propTypes = {
  form: PropTypes.any,
  visible: PropTypes.bool.isRequired,
  formData: PropTypes.object.isRequired,
  onOk: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

const WrappedRewardForm = Form.create({ name: 'RewardForm' })(
  RewardForm
)
export default WrappedRewardForm
